// External Dependencies
import { FC } from 'react';
import { useSelector } from 'react-redux';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItem, { type ListItemProps } from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountTieIcon from 'mdi-material-ui/AccountTie';
import AdminIcon from 'mdi-material-ui/ShieldAccount';
import SendIcon from '@mui/icons-material/Send';
import styled from 'styled-components';

// Internal Dependencies
import { getUsersByFraudAlertFlag } from 'gql/queries/alertFlags';
import { CONTACT_INFO } from 'utils/constants';
import { getFullName } from 'utils';
import { isMobileOrTabletScreenSize } from 'state/selectors';

// Local Dependencies
import BecomeThisUserButton from '../BecomeThisUserButton';
import EnhancedButton from '../EnhancedButton';
import PersonListItemMoreActionsIconMenu from '../PersonListItemMoreActionsIconMenu';

// Local Typings
interface Props extends ListItemProps {
  adminPortalId?: string;
  userData: GQL.IUser;
  variant: 'district_administrator' | 'district_assistant' | 'director';
}

// Local Variables
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.prestoPrimaryMain,
}));

const StyledTypography = styled(Typography)({
  // We are setting a `<span>` to block to avoid HTML warnings
  //  since a `<p>` cannot have a nested `<div>`
  display: 'block',
  lineHeight: 1.6,
}) as typeof Typography;

const getIconAndRole = (variant: Props['variant']) => {
  let Icon = AccountTieIcon;
  let role = 'Director';

  switch (variant) {
    case 'district_administrator':
      Icon = AdminIcon;
      role = 'Admin';
      break;
    case 'district_assistant':
      Icon = AdminIcon;
      role = 'Admin Assistant';
      break;
    case 'director':
      Icon = AccountTieIcon;
      role = 'Director';
      break;
    default:
      Icon = AccountTieIcon;
      role = 'Director';
      break;
  }

  return {
    Icon,
    role,
  };
};

// Component Definition
const PersonListItem: FC<Props> = ({
  adminPortalId,
  userData,
  variant,
  ...otherProps
}) => {
  const isMobileOrTabletScreen = useSelector(isMobileOrTabletScreenSize);

  const isDirector = variant === 'director';

  const {
    authUserId,
    email,
    id,
    phoneNumber,
  } = userData;

  const {
    Icon,
    role,
  } = getIconAndRole(variant);

  const { data } = getUsersByFraudAlertFlag();

  const userIsFlagged = data?.usersByAlertFlag.filter((user) => user.id === authUserId).length;

  const userFullName = getFullName(userData);

  return (
    <ListItem
      {...otherProps}
      alignItems="flex-start"
      key={id}
    >
      <ListItemAvatar>
        <StyledAvatar>
          <Icon />
        </StyledAvatar>
      </ListItemAvatar>

      <ListItemText
        primary={`${userFullName}${!isDirector && role ? ` — ${role}` : ''}`}
        secondary={(
          <>
            <StyledTypography
              color="textPrimary"
              component="span"
              variant="body2"
            >
              {userIsFlagged ? (
                <Box
                  component="span"
                  marginLeft={1.5}
                >
                  <Chip
                    color="error"
                    component="span"
                    label="Fraud"
                    size="small"
                  />
                </Box>
              ) : null}
            </StyledTypography>

            {userData.title && (
              <StyledTypography
                color="textPrimary"
                component="span"
                variant="body2"
              >
                {userData.title}
              </StyledTypography>
            )}

            <StyledTypography
              component="span"
              variant="body2"
            >
              {email}
            </StyledTypography>

            <StyledTypography
              component="span"
              variant="body2"
            >
              {phoneNumber}
            </StyledTypography>
          </>
        )}
      />

      <ListItemSecondaryAction sx={{ alignItems: 'center', display: 'flex' }}>
        <BecomeThisUserButton userId={id} />

        {isMobileOrTabletScreen ? (
          <Tooltip
            arrow
            title="Send Email"
          >
            <IconButton
              href={`mailto:${email}?cc=${CONTACT_INFO.SUPPORT_EMAIL}`}
              rel="noreferrer noopener"
              size="large"
              target="_blank"
            >
              <SendIcon color="primary" />
            </IconButton>
          </Tooltip>
        ) : (
          <EnhancedButton
            color="primary"
            href={`mailto:${email}?cc=${CONTACT_INFO.SUPPORT_EMAIL}`}
            rel="noreferrer noopener"
            size="small"
            startIcon={<SendIcon fontSize="small" />}
            sx={{ marginRight: 1 }}
            target="_blank"
            variant="outlined"
          >
            Email
          </EnhancedButton>

        )}

        <PersonListItemMoreActionsIconMenu
          adminPortalId={adminPortalId ?? ''}
          authUserId={authUserId}
          memberId={id}
          userFullName={userFullName}
          variant={variant}
        />
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default PersonListItem;
